import { useQuery } from "@tanstack/react-query";
import type { PlannerLensResult, PlannerGap } from "@shared/planner-lens";

/**
 * PLANNER-LENS-1 Phase B (s152) — the planner's "what your executor still needs"
 * gap list. Consumes the engine-computed, jurisdiction-agnostic
 * `GET /api/clients/:clientId/planner-lens` (server/planner-lens.ts). Read-only.
 *
 * Both the dashboard gap card AND the per-field inline badges read THIS query — same
 * key, so TanStack dedupes the fetch and the type lives once. The badges use
 * `gapsByRef` for O(1) lookup by `<section-slug>:<field_key>`.
 */
/**
 * EXECUTOR-GAP-PANEL-1 (s199) — the failure, carrying its STATUS.
 *
 * The panel must say something different for 404, 403 and 500 (phase 2b). Before this the query
 * threw a single "Failed to load", which is the shape `WIE-ERROR-COPY-1` records as having turned
 * the Where-Is-Everything investigation into a classification exercise instead of a diagnosis: one
 * message for three unrelated causes tells the reader nothing and tells us nothing.
 *
 * 🔴 Status only — never a response body. This error can reach Sentry, and a thrown Error's message
 * is sent verbatim (CLAUDE.md § Logging).
 */
export class PlannerLensError extends Error {
  constructor(public readonly status: number) {
    super(`planner-lens request failed (${status})`);
    this.name = "PlannerLensError";
  }
}

export function usePlannerLens(clientId: string | undefined) {
  return useQuery<PlannerLensResult>({
    queryKey: ["/api/clients", clientId, "planner-lens"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/planner-lens`);
      if (!res.ok) throw new PlannerLensError(res.status);
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * Index the gap list by its planner ref (`<section-slug>:<field_key>`) for O(1)
 * per-field badge lookup. Refs are unique in the engine output (gaps are merged by
 * ref), so a plain Map is exact — the last-wins guard is defensive only.
 */
export function gapsByRef(data: PlannerLensResult | undefined): Map<string, PlannerGap> {
  const map = new Map<string, PlannerGap>();
  for (const g of data?.gaps ?? []) map.set(g.ref, g);
  return map;
}
